import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import GetInTouchModal from './GetInTouchModal';
import { prefetchRoute } from '../routes';

const Header = () => {
    const { t } = useTranslation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { name: t('nav.home'), href: '/' },
        { name: t('nav.creators'), href: '/creator' },
        { name: t('nav.viewers'), href: '/viewer' },
        { name: t('nav.hoisters'), href: '/hoister' },
        { name: t('nav.about'), href: '/about' },
        { name: t('nav.contact'), href: '/contact' },
    ];

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'glass-nav shadow-xl' : 'bg-transparent'
                    }`}
            >
                <nav className="container-custom">
                    <div className="flex items-center justify-between h-16 lg:h-20">
                        {/* Logo */}
                        <Link
                            to="/"
                            onMouseEnter={() => prefetchRoute('/')}
                            className="flex items-center space-x-3 group mr-4"
                        >
                            <img
                                src="/logo.png"
                                alt="NetVid Logo"
                                className="w-16 h-16 object-contain transition-transform duration-300 group-hover:scale-110"
                            />
                            <span className="text-2xl font-bold font-display text-primary hidden sm:block">
                                NetVid
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center space-x-6">
                            {navItems.map((item) => (
                                <Link
                                    key={item.href}
                                    to={item.href}
                                    onMouseEnter={() => prefetchRoute(item.href)}
                                    className="text-light hover:text-primary transition-colors duration-300 font-medium whitespace-nowrap"
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>

                        {/* Controls & CTA */}
                        <div className="flex items-center space-x-4">
                            <LanguageSelector />
                            <div className="hidden md:block">
                                <button
                                    onClick={() => setIsContactModalOpen(true)}
                                    className="btn-primary"
                                >
                                    {t('nav.contact')}
                                </button>
                            </div>

                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="lg:hidden p-2 text-light hover:text-primary transition-colors"
                            >
                                {isMobileMenuOpen ? (
                                    <X className="w-6 h-6" />
                                ) : (
                                    <Menu className="w-6 h-6" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    <AnimatePresence>
                        {isMobileMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="lg:hidden overflow-hidden"
                            >
                                <div className="py-6 space-y-4 px-2">
                                    {navItems.map((item) => (
                                        <Link
                                            key={item.href}
                                            to={item.href}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className="block text-light hover:text-primary transition-colors duration-300 font-medium py-3 text-lg border-b border-white/5"
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                    <div className="pt-6">
                                        <button
                                            onClick={() => {
                                                setIsContactModalOpen(true);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="btn-primary w-full py-4 text-center"
                                        >
                                            {t('nav.contact')}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </nav>
            </header>

            {/* Get in Touch Modal */}
            <GetInTouchModal
                isOpen={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
            />
        </>
    );
};

export default Header;
